import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const SeccionTendencias = ({ comparativoMensual, topProductos, anioActivo, metricaActiva, infoFiltros }) => {
    const [isOpen, setIsOpen] = useState(false);

    const formatMoney = (value) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value);
    const formatNumber = (value) => new Intl.NumberFormat('es-PE').format(value);
    const formatValue = (value) => metricaActiva === 'monto' ? formatMoney(value) : formatNumber(value);

    // Top 5 Productos
    const top5 = topProductos ? topProductos.slice(0, 5) : [];

    // Colores para el borde de cada tarjeta top
    const getBorderColor = (index) => {
        if (index === 0) return '#ffc107'; // Oro
        if (index === 1) return '#adb5bd'; // Plata
        if (index === 2) return '#cd7f32'; // Bronce
        return '#007bff'; // Azul para el resto
    };

    return (
        <div className="shadow-sm mb-4" style={{ border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
            <div
                className="card-header border-bottom p-4 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', backgroundColor: '#35605a', borderRadius: '1.25rem' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div>
                    <h5 className="mb-0 font-weight-bold text-white"><i className="fas fa-chart-line text-white mr-2"></i> Tendencia Mensual y Top Productos</h5>
                    {infoFiltros && <div className="mt-1"><small className="text-white"><i className="fas fa-filter mr-1"></i> Filtrando por: {infoFiltros}</small></div>}
                </div>
                <button className="btn btn-light btn-sm rounded-circle"><i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i></button>
            </div>
            {isOpen && (
                <div className="card-body p-4">
                    {/* Top 5 Productos */}
                    <h6 className="font-weight-bold text-muted mb-3 text-uppercase">Top 5 Productos Más Vendidos</h6>
                    <div className="row mb-5">
                        {top5.map((prod, index) => (
                            <div key={prod.producto_id} className="col-12 col-sm-6 col-lg pb-2">
                                <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                    <div className="card-body p-3">
                                        {/* <div className="position-absolute" style={{ top: '-5px', right: '-5px', fontSize: '4.5rem', fontWeight: '900', color: getBorderColor(index), opacity: 0.1, lineHeight: 1, pointerEvents: 'none' }}>
                                            #{index + 1}
                                        </div> */}
                                        <div className="d-flex align-items-center gap-3 mb-2">
                                            <div className="d-flex align-items-center justify-content-center rounded-circle shadow-sm flex-shrink-0"
                                                style={{
                                                    width: '38px',
                                                    height: '38px',
                                                    backgroundColor: getBorderColor(index),
                                                    color: index === 0 ? '#fff' : '#ffffffff',
                                                    fontWeight: 'bold',
                                                    fontSize: '1.2rem',
                                                    border: index === 0 ? '2px solid #FFD700' : 'none',
                                                }}
                                            >
                                                {index === 0 ? <i className="fas fa-trophy" style={{ color: '#ffffffff' }}></i> : index + 1}
                                            </div>

                                            <h6 className="font-weight-bold text-white mx-2 flex-grow-1"
                                                title={prod.producto_nombre}
                                                style={{
                                                    fontSize: '0.95rem',
                                                    lineHeight: '1.4',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {prod.producto_nombre}
                                            </h6>
                                        </div>
                                        <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                            <span className='text-white'>Soles:</span>
                                            <span className="font-weight-bold text-white">{formatMoney(prod.ingresos_totales)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.75rem' }}>
                                            <span className='text-white'>Unidades:</span>
                                            <span className="font-weight-bold text-white">{formatNumber(prod.cantidad_total)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {top5.length === 0 && (
                            <div className="col-12 text-center text-muted p-3">
                                No hay productos para mostrar en el top 5 con los filtros actuales.
                            </div>
                        )}
                    </div>

                    {/* Gráfico de Tendencia */}
                    <h6 className="font-weight-bold text-muted mb-3 text-uppercase">Comparativo de ventas {parseInt(anioActivo) - 1} vs. {anioActivo}</h6>
                    <div style={{ width: '100%', height: 350 }}>
                        <ResponsiveContainer>
                            <AreaChart data={comparativoMensual} margin={{ top: 20, right: 30, left: 20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorAnterior" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#888' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#888' }} tickFormatter={(val) => metricaActiva === 'monto' ? `${val / 1000}k` : val} />
                                <Tooltip
                                    formatter={(value) => formatValue(value)}
                                    contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: '10px 15px' }}
                                    cursor={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 2 }}
                                />
                                <Legend verticalAlign="top" height={36} iconType="circle" />
                                <Area type="monotone" name={`Ventas ${anioActivo}`} dataKey={`anio_${anioActivo}_${metricaActiva}`} stroke="#8884d8" strokeWidth={3} fillOpacity={1} fill="url(#colorActual)" animationDuration={1500} />
                                <Area type="monotone" name={`Ventas ${parseInt(anioActivo) - 1}`} dataKey={`anio_${parseInt(anioActivo) - 1}_${metricaActiva}`} stroke="#82ca9d" strokeWidth={2} fillOpacity={1} fill="url(#colorAnterior)" animationDuration={1500} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeccionTendencias;
